import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { CHART_PALETTE, COLORS } from "../../../components/dashboard/shared/DashboardShared";
import { formatCount, formatYmd, formatYmdShort, minutesToHHmm, resolveOperatorName } from "./format";

// Gráfico principal: cada punto es un grupo de eventos (día × minuto del día).
//
// Eje X: el día. Se usa el ÍNDICE del día dentro del período, no una fecha
// parseada: `new Date("2026-08-01")` se interpreta como UTC y en Chile/
// Argentina dibujaría todo corrido un día. El índice es aritmética pura y el
// tickFormatter parte el string "YYYY-MM-DD" para etiquetarlo.
//
// Eje Y: minuto del día tal como lo mandó el backend (0..1440), ya en la hora
// local del comercio. Los ticks se formatean con aritmética entera.

const MAX_X_TICKS = 8;
const Y_TICKS = [0, 180, 360, 540, 720, 900, 1080, 1260, 1440];
const POINT_SIZE_RANGE = [14, 90];
const POINT_OPACITY = 0.45;

const YMD = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Todos los días del período, incluidos los que no tuvieron ni una entrega.
 *
 * Sin esto el eje X solo tendría los días CON datos y una semana sin operación se
 * colapsaría: dos puntos separados por diez días quedarían pegados y el gráfico
 * mostraría una continuidad que no existió. Los huecos son información.
 *
 * La aritmética va en UTC de punta a punta (`Date.UTC` + `getUTC*`): así no interviene
 * la zona del navegador en ningún paso. Es el mismo motivo por el que el resto del
 * archivo no parsea fechas — acá no se puede evitar, pero sí se puede hacer
 * determinista.
 *
 * @param {string} startDate "YYYY-MM-DD"
 * @param {string} endDate   "YYYY-MM-DD"
 * @returns {string[]} días consecutivos, o `[]` si el rango no es utilizable
 */
export const buildDayRange = (startDate, endDate) => {
  if (!YMD.test(startDate || "") || !YMD.test(endDate || "")) return [];

  const [ys, ms, ds] = startDate.split("-").map(Number);
  const [ye, me, de] = endDate.split("-").map(Number);
  let cursor = Date.UTC(ys, ms - 1, ds);
  const last = Date.UTC(ye, me - 1, de);
  if (cursor > last) return [];

  const days = [];
  // Cota de seguridad: el backend ya rechaza rangos > 92 días, pero un rango absurdo
  // no debe poder colgar el render.
  const MAX_DAYS = 400;
  while (cursor <= last && days.length < MAX_DAYS) {
    const day = new Date(cursor);
    const month = String(day.getUTCMonth() + 1).padStart(2, "0");
    const date = String(day.getUTCDate()).padStart(2, "0");
    days.push(`${day.getUTCFullYear()}-${month}-${date}`);
    cursor += 86400000;
  }
  return days;
};

/**
 * Ticks del eje X repartidos parejo, sin repetir el último.
 * @param {number} dayCount
 * @returns {number[]}
 */
const buildXTicks = (dayCount) => {
  if (dayCount <= 0) return [];
  const step = Math.max(1, Math.ceil(dayCount / MAX_X_TICKS));
  const ticks = [];
  for (let i = 0; i < dayCount; i += step) ticks.push(i);
  if (ticks[ticks.length - 1] !== dayCount - 1) ticks.push(dayCount - 1);
  return ticks;
};

const ScatterTooltip = ({ active, payload }) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  return (
    <div className="ci-chart-tooltip">
      <p className="ci-chart-tooltip__title">
        {formatYmd(point.d)} · {minutesToHHmm(point.m)}
      </p>
      <p className="ci-chart-tooltip__row">
        <span className="ci-chart-tooltip__dot" style={{ background: point.color }} />
        {point.seriesName}
      </p>
      <p className="ci-chart-tooltip__row">
        {point.isFailed ? t("Intentos fallidos") : t("Entregas")}: <strong>{formatCount(point.c)}</strong>
      </p>
    </div>
  );
};

/**
 * @param {{
 *   buckets: Array<{d:string,m:number,t:string,op:string,c:number}>,
 *   operators: Array<{key:string,name:string,isOwnFleet:boolean}>,
 *   cutoffHour: number,
 *   isDark: boolean,
 *   rangeStart?: string,
 *   rangeEnd?: string,
 *   truncated?: boolean,
 *   height?: number
 * }} props
 */
export default function DeliveryScatterChart({
  buckets,
  operators,
  cutoffHour,
  isDark,
  rangeStart,
  rangeEnd,
  truncated = false,
  height = 420,
}) {
  const { t } = useTranslation();

  const tickColor = isDark ? "#D1D5DB" : "#6B7280";
  const gridColor = isDark ? "#374151" : "#E5E7EB";

  // Eje X = el período completo, para que un día sin entregas se vea como el hueco
  // que fue. El orden lexicográfico de "YYYY-MM-DD" ya es el cronológico.
  //
  // EXCEPCIÓN: cuando el backend truncó y devolvió una MUESTRA de días, los huecos
  // serían artificiales — días que sí tuvieron entregas se verían vacíos. En ese caso
  // se cae a los días presentes, y el aviso de "muestra de días" explica el resto.
  const days = useMemo(() => {
    if (!truncated) {
      const full = buildDayRange(rangeStart, rangeEnd);
      if (full.length > 0) return full;
    }
    const unique = new Set((buckets || []).map((bucket) => bucket.d));
    return Array.from(unique).sort();
  }, [buckets, rangeStart, rangeEnd, truncated]);

  const dayIndex = useMemo(() => new Map(days.map((day, index) => [day, index])), [days]);

  const series = useMemo(() => {
    const list = buckets || [];
    const knownKeys = new Set((operators || []).map((operator) => operator.key));

    const toPoint = (bucket, seriesName, color, isFailed) => ({
      x: dayIndex.get(bucket.d),
      y: bucket.m,
      z: bucket.c,
      d: bucket.d,
      m: bucket.m,
      c: bucket.c,
      seriesName,
      color,
      isFailed,
    });

    const result = (operators || []).map((operator, index) => {
      const name = resolveOperatorName(operator, t);
      const color = CHART_PALETTE[index % CHART_PALETTE.length];
      return {
        key: operator.key,
        name,
        color,
        points: list
          .filter((bucket) => bucket.t === "delivered" && bucket.op === operator.key)
          .map((bucket) => toPoint(bucket, name, color, false)),
      };
    });

    // Defensivo: entregas cuyo operador no vino en `operators`. Agruparlas es
    // preferible a descartarlas en silencio — si el backend suma una clave
    // nueva, el punto se sigue viendo.
    const orphanName = t("Otros");
    const orphanColor = CHART_PALETTE[CHART_PALETTE.length - 1];
    const orphans = list
      .filter((bucket) => bucket.t === "delivered" && !knownKeys.has(bucket.op))
      .map((bucket) => toPoint(bucket, orphanName, orphanColor, false));
    if (orphans.length > 0) {
      result.push({ key: "__orphans__", name: orphanName, color: orphanColor, points: orphans });
    }

    const failedName = t("Intentos fallidos");
    const failed = list
      .filter((bucket) => bucket.t === "failed")
      .map((bucket) => toPoint(bucket, failedName, COLORS.danger, true));
    if (failed.length > 0) {
      result.push({ key: "__failed__", name: failedName, color: COLORS.danger, points: failed });
    }

    return result.filter((entry) => entry.points.length > 0);
  }, [buckets, operators, dayIndex, t]);

  const xTicks = useMemo(() => buildXTicks(days.length), [days.length]);
  const cutoffMinutes = typeof cutoffHour === "number" ? cutoffHour * 60 : null;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ScatterChart margin={{ top: 16, right: 24, bottom: 8, left: 0 }}>
        <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
        <XAxis
          type="number"
          dataKey="x"
          domain={[-0.5, Math.max(0.5, days.length - 0.5)]}
          ticks={xTicks}
          tickFormatter={(index) => formatYmdShort(days[index])}
          tick={{ fill: tickColor, fontSize: 11 }}
          stroke={gridColor}
          allowDecimals={false}
          name={t("Día")}
        />
        <YAxis
          type="number"
          dataKey="y"
          domain={[0, 1440]}
          ticks={Y_TICKS}
          tickFormatter={minutesToHHmm}
          tick={{ fill: tickColor, fontSize: 11 }}
          stroke={gridColor}
          width={56}
          name={t("Hora")}
        />
        {/* domain fijo desde 0: si todos los buckets tienen el mismo conteo, el
            dominio automático queda degenerado (min === max). */}
        <ZAxis type="number" dataKey="z" domain={[0, "dataMax"]} range={POINT_SIZE_RANGE} name={t("Eventos")} />
        {cutoffMinutes !== null && (
          <ReferenceLine
            y={cutoffMinutes}
            stroke={COLORS.danger}
            strokeDasharray="6 4"
            ifOverflow="extendDomain"
            label={{
              value: t("Corte {{hora}}", { hora: minutesToHHmm(cutoffMinutes) }),
              position: "insideTopRight",
              fill: COLORS.danger,
              fontSize: 11,
            }}
          />
        )}
        <Tooltip content={<ScatterTooltip />} cursor={{ strokeDasharray: "3 3" }} />
        <Legend iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
        {series.map((entry) => (
          <Scatter
            key={entry.key}
            name={entry.name}
            data={entry.points}
            fill={entry.color}
            fillOpacity={POINT_OPACITY}
            stroke="none"
            isAnimationActive={false}
          />
        ))}
      </ScatterChart>
    </ResponsiveContainer>
  );
}
